import React, { useRef, useEffect } from 'react';
import { WaterDropAnimation } from './animations/WaterDropAnimation';
import { useRippleEffect } from '../hooks/useRippleEffect';
import saasVideo from '../assets/SAASMontage.mp4';
import './Hero.css';

export const Hero: React.FC = () => {
  const { ripples, createRipple } = useRippleEffect();
  const videoRef = useRef<HTMLVideoElement>(null);
  
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = true;
      video.play().catch(() => {});
    }
  }, []);

  const handleRippleCreate = (x: number, y: number) => {
    // Optional: Add any additional effects when ripples are created
    console.log(`Ripple created at: ${x}, ${y}`);
  };

  const handleGetStarted = () => {
    // Navigate to contact section
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero" onClick={createRipple}>
      {/* Realistic Water Drop Animation Background */}
      <WaterDropAnimation onRippleCreate={handleRippleCreate} />

      <div className="hero-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="hero-container">
        <div className="hero-content">
          <div className="hero-tagline-top">Unlock what your customers want before they ask</div>
          
          <h1 className="hero-title">
            <span className="hero-highlight">Our <span className="spotton-brand">
                <span className="letter-s">S</span>
                <span className="letter-p">p</span>
                <span className="letter-o1">o</span>
                <span className="letter-t1">t</span>
                <span className="letter-t2">t</span>
                <span className="letter-hyphen">-</span>
                <span className="letter-o2">o</span>
                <span className="letter-n">n</span>
              </span> Finds What Customers <span className="hero-highlight-underline">Need</span>.</span>
          </h1>
          
          <p className="hero-description">
            <span className="spotton-brand-text">Spott-on</span>, Intelyx’s AI discovery engine for B2B distribution companies, reveals what your customers produce and what you should sell next—helping sales teams accelerate revenue growth.
          </p>

          <div className="hero-tagline-bottom">Find more. Sell more. Grow more.</div>

          <div className="hero-actions">
            <button className="btn-hero-primary" onClick={handleGetStarted}>
              Get Started Today
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path 
                  d="M4 10h12m-6-6l6 6-6 6" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="hero-visual">
          {/* Product montage video */}
          <video
            ref={videoRef}
            className="hero-video"
            autoPlay
            muted
            loop
            playsInline
            width={470}
            height={394}
          >
            <source src={saasVideo} type="video/mp4" />
          </video>

          {/* Original AI visualization (preserved for easy revert — hidden via CSS) */}
          <div className="ai-visualization">
            <div className="central-node">
              <div className="node-core"></div>
              <div className="node-ring"></div>
              <div className="node-outer-ring"></div>
            </div>
            
            <div className="connection-lines">
              <div className="line line-1"></div>
              <div className="line line-2"></div>
              <div className="line line-3"></div>
              <div className="line line-4"></div>
            </div>

            <div className="satellite-nodes">
              <div className="satellite-node node-1"></div>
              <div className="satellite-node node-2"></div>
              <div className="satellite-node node-3"></div>
              <div className="satellite-node node-4"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Ripple effects */}
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="section-ripple"
          style={{
            left: ripple.x,
            top: ripple.y,
          }}
        >
          <div className="ripple-ring ring-1"></div>
          <div className="ripple-ring ring-2"></div>
          <div className="ripple-ring ring-3"></div>
        </div>
      ))}
    </section>
  );
};